import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { invitationTypeFormSchema, type InvitationTypeFormValues } from './invitationTypeFormSchema'
import type { InvitationType, InvitationTypeUpsert } from '../types'

interface Props {
  visible: boolean
  initial: InvitationType | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: InvitationTypeUpsert) => void
  onHide: () => void
}

/** Davet türü ekle/düzenle formu (025: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function InvitationTypeFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(invitationTypeFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: InvitationTypeFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={
        initial
          ? t('activity.invitationTypes.form.editTitle')
          : t('activity.invitationTypes.form.newTitle')
      }
      submitLabel={t('activity.invitationTypes.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('activity.invitationTypes.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('activity.invitationTypes.form.nameEn')}
        />
      </div>
    </FormDialog>
  )
}
